<template>
  <div data-testid="presence-panel" class="flex flex-col gap-4 p-4">
    <div class="flex items-center justify-between">
      <p class="text-sm text-gray-500">
        Raw data of the new presence pipeline (DevicePresenceState and the newest DevicePresenceEvents). Read-only.
      </p>
      <button
        type="button"
        data-testid="presence-refresh"
        class="inline-flex items-center p-1 text-gray-700 border border-transparent rounded-full hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        title="Refresh"
        @click="load"
      >
        <RefreshIcon :class="[loading ? 'animate-reverse-spin' : '', 'h-5 w-5']" aria-hidden="true" />
        <span class="sr-only">Refresh</span>
      </button>
    </div>

    <div v-if="loading && !presence" data-testid="presence-loading" class="py-16 text-center">
      <div
        class="inline-block w-6 h-6 border-4 border-blue-500 border-solid rounded-full animate-spin border-r-transparent"
      ></div>
    </div>

    <p v-else-if="failed" data-testid="presence-error" class="py-16 text-sm text-center text-gray-500">
      The presence data could not be loaded. Try again with the refresh button.
    </p>

    <template v-else-if="presence">
      <div data-testid="presence-state" class="px-4 py-3 rounded-lg shadow-md state_unknown">
        <template v-if="presence.state">
          <div class="flex flex-wrap items-center gap-2">
            <span
              :class="[badgeClass(presence.state.connected), 'px-2 py-0.5 text-xs font-medium rounded-full']"
              data-testid="presence-state-badge"
            >
              {{ presence.state.connected ? "connected" : "disconnected" }}
            </span>
            <span class="text-sm"
              >since <time :title="utc(presence.state.since)">{{ local(presence.state.since) }}</time></span
            >
          </div>
          <dl class="grid grid-cols-2 mt-3 text-sm gap-x-6 gap-y-2 md:grid-cols-5">
            <div>
              <dt class="text-gray-500">Session</dt>
              <dd class="font-mono break-all">{{ presence.state.sessionIdentifier || "-" }}</dd>
            </div>
            <div>
              <dt class="text-gray-500">Disconnect reason</dt>
              <dd>{{ presence.state.disconnectReason || "-" }}</dd>
            </div>
            <div>
              <dt class="text-gray-500">Escalation stage</dt>
              <dd data-testid="presence-state-stage">{{ presence.state.escalationStage }}</dd>
            </div>
            <div>
              <dt class="text-gray-500">Next escalation</dt>
              <dd>
                <time v-if="presence.state.nextEscalationAt" :title="utc(presence.state.nextEscalationAt)">{{
                  local(presence.state.nextEscalationAt)
                }}</time>
                <span v-else>-</span>
              </dd>
            </div>
            <div>
              <dt class="text-gray-500">Revision</dt>
              <dd>{{ presence.state.revision }}</dd>
            </div>
          </dl>
        </template>
        <p v-else data-testid="presence-no-state" class="text-sm">
          No state yet: the pipeline has not derived a state for this device.
        </p>
      </div>

      <p v-if="!presence.events.length" data-testid="presence-empty" class="py-8 text-sm text-center text-gray-500">
        No presence events recorded for this device yet.
      </p>
      <div v-else class="overflow-x-auto">
        <table class="min-w-full text-sm text-left divide-y divide-gray-200">
          <caption class="mb-2 text-sm font-medium text-left text-gray-900">
            Newest
            {{
              presence.events.length
            }}
            presence events
          </caption>
          <thead class="text-xs text-gray-500 uppercase">
            <tr>
              <th scope="col" class="px-3 py-2 font-medium">Time</th>
              <th scope="col" class="px-3 py-2 font-medium">Event</th>
              <th scope="col" class="px-3 py-2 font-medium">Session</th>
              <th scope="col" class="px-3 py-2 font-medium">Disconnect reason</th>
              <th scope="col" class="px-3 py-2 font-medium">Client initiated</th>
              <th scope="col" class="px-3 py-2 font-medium">Version</th>
              <th scope="col" class="px-3 py-2 font-medium">Source</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr
              v-for="event in presence.events"
              :key="`${event.timestamp}#${event.eventType}#${event.sessionIdentifier || ''}`"
              :class="SOURCE_STYLES[event.source]?.row"
              data-testid="presence-event"
            >
              <td class="px-3 py-2 whitespace-nowrap">
                <time :title="utc(event.timestamp)">{{ local(event.timestamp) }}</time>
              </td>
              <td class="px-3 py-2">
                <span
                  :class="[badgeClass(event.eventType === 'connected'), 'px-2 py-0.5 text-xs font-medium rounded-full']"
                  >{{ event.eventType }}</span
                >
              </td>
              <td class="px-3 py-2 font-mono" :title="event.sessionIdentifier || undefined">
                {{ shorten(event.sessionIdentifier) }}
              </td>
              <td class="px-3 py-2">{{ event.disconnectReason || "-" }}</td>
              <td class="px-3 py-2">{{ yesNo(event.clientInitiatedDisconnect) }}</td>
              <td class="px-3 py-2">{{ event.versionNumber ?? "-" }}</td>
              <td class="px-3 py-2">
                <span
                  v-if="SOURCE_STYLES[event.source]"
                  :class="[SOURCE_STYLES[event.source].badge, 'px-2 py-0.5 text-xs font-medium rounded-full']"
                  :title="SOURCE_STYLES[event.source].hint"
                  data-testid="presence-event-source-marked"
                  >{{ event.source }}</span
                >
                <span v-else class="text-gray-500">{{ event.source }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import { onMounted, ref } from "vue";
import { useStore } from "vuex";
import { RefreshIcon } from "@heroicons/vue/outline";

// Events that did not come live from AWS IoT are marked, so corrections stand out.
const SOURCE_STYLES = {
  reconciler: {
    row: "bg-yellow-50",
    badge: "bg-yellow-100 text-yellow-800",
    hint: "Written by the hourly reconciler to correct a lost event",
  },
  timestream_backfill: {
    row: "bg-sky-50",
    badge: "bg-sky-100 text-sky-800",
    hint: "Copied from the old TimeStream history",
  },
};

const utc = (iso) => iso.replace("T", " ").replace("+00:00", " UTC");

export default {
  name: "DevicePresencePanel",
  components: { RefreshIcon },
  props: {
    deviceId: { type: String, required: true },
  },
  setup(props) {
    const store = useStore();
    const presence = ref(null);
    const loading = ref(false);
    const failed = ref(false);

    const load = async () => {
      loading.value = true;
      try {
        const result = await store.dispatch("device/getDevicePresence", { deviceId: props.deviceId });
        failed.value = !result;
        presence.value = result || null;
      } finally {
        loading.value = false;
      }
    };

    onMounted(load);

    const local = (iso) => new Date(iso).toLocaleString("de-DE");
    const shorten = (value) => (!value ? "-" : value.length > 12 ? `${value.slice(0, 8)}…` : value);
    const yesNo = (value) => (value === null || value === undefined ? "-" : value ? "yes" : "no");
    const badgeClass = (connected) => (connected ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800");

    return { presence, loading, failed, load, local, utc, shorten, yesNo, badgeClass, SOURCE_STYLES };
  },
};
</script>
